import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, X, ImageIcon, Upload, Loader2, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";

const emptyDraft = {
  slug: "",
  name: "",
  category: "Portraits",
  description: "",
  image_url: "",
  deposit_percent: 25,
  is_active: true,
  sort_order: 99,
  tiers: [{ name: "Bronze", price: 0, duration_minutes: 60, description: "", deliverables: [] }],
};

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service object or null
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // service pending delete confirmation

  async function load() {
    setLoading(true);
    try {
      const [{ data }, deletedRes] = await Promise.all([
        api.get("/admin/services"),
        api.get("/admin/services/deleted").catch(() => ({ data: [] })),
      ]);
      setServices(data);
      setDeleted(deletedRes.data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(svc) {
    await save(svc, { ...svc, is_active: !svc.is_active }, "Service updated");
  }

  async function save(original, draft, successMsg = "Saved", opts = {}) {
    try {
      const payload = {
        slug: draft.slug,
        name: draft.name,
        category: draft.category,
        description: draft.description,
        image_url: draft.image_url,
        deposit_percent: Number(draft.deposit_percent) || 0,
        is_active: !!draft.is_active,
        sort_order: Number(draft.sort_order) || 0,
        tiers: (draft.tiers || []).map((t) => ({
          name: t.name,
          price: Number(t.price) || 0,
          duration_minutes: Number(t.duration_minutes) || 60,
          description: t.description || "",
          deliverables: t.deliverables || [],
        })),
      };
      if (original?.id) {
        await api.patch(`/admin/services/${original.id}`, payload);
      } else {
        await api.post("/admin/services", payload);
      }
      if (!opts.silent) {
        if (successMsg) toast.success(successMsg);
        setEditing(null);
        setCreating(false);
        load();
      }
    } catch (err) {
      if (!opts.silent) toast.error(formatApiErrorDetail(err.response?.data?.detail));
      throw err;
    }
  }

  // Uses a shadcn AlertDialog instead of window.confirm() — Safari/iOS can
  // silently dismiss native confirm dialogs, leaving the button unresponsive.
  async function remove(svc) {
    try {
      await api.delete(`/admin/services/${svc.id}`);
      toast.success("Package moved to Recently Deleted — restorable for 30 days");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConfirmDelete(null);
    }
  }

  async function restore(svc) {
    try {
      await api.post(`/admin/services/${svc.id}/restore`);
      toast.success(`"${svc.name}" restored`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  // Move a service up or down using a single atomic reorder request.
  // The previous two-PATCH approach could leave duplicate sort_orders if one
  // request succeeded and the other failed, permanently breaking future moves.
  async function move(svc, direction) {
    const sorted = [...services].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex((s) => s.id === svc.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    // Build the new order by swapping the two items
    const newOrder = [...sorted];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];

    // Optimistic UI update — re-assign sequential sort_orders so the list
    // stays consistent even before the server confirms.
    setServices(newOrder.map((s, i) => ({ ...s, sort_order: i })));

    try {
      await api.post("/admin/services/reorder", { ids: newOrder.map((s) => s.id) });
    } catch (err) {
      toast.error("Couldn't reorder — reloading");
      load();
    }
  }

  return (
    <div className="space-y-8" data-testid="admin-services-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow mb-2">Services & Pricing</div>
          <h1 className="font-serif text-4xl font-light">Photoshoot packages</h1>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90"
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4" /> Add package
        </button>
      </div>

      {loading && <div className="text-muted-foreground">Loading…</div>}

      <div className="grid lg:grid-cols-2 gap-6">
        {services.map((s, idx) => (
          <div key={s.id} className="bg-card border border-border" data-testid={`admin-service-${s.slug}`}>
            <div className="flex gap-4 p-5 border-b border-border">
              <div className="flex flex-col gap-1 -mr-1 self-stretch justify-center">
                <button
                  onClick={() => move(s, "up")}
                  disabled={idx === 0}
                  className="w-7 h-7 inline-flex items-center justify-center border border-border hover:border-foreground/60 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move up"
                  title="Move up"
                  data-testid={`move-up-${s.slug}`}
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => move(s, "down")}
                  disabled={idx === services.length - 1}
                  className="w-7 h-7 inline-flex items-center justify-center border border-border hover:border-foreground/60 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move down"
                  title="Move down"
                  data-testid={`move-down-${s.slug}`}
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
              <img src={s.image_url} alt={s.name} className="w-20 h-24 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="eyebrow">{s.category}</div>
                <div className="font-serif text-xl mt-1">{s.name}</div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <button
                  onClick={() => toggleActive(s)}
                  className={`text-xs uppercase tracking-[0.18em] px-3 h-8 border ${s.is_active ? "border-emerald-500/50 text-emerald-700" : "border-border text-muted-foreground"}`}
                  data-testid={`toggle-active-${s.slug}`}
                >
                  {s.is_active ? "Active" : "Inactive"}
                </button>
              </div>
            </div>

            <div className="p-5 border-b border-border">
              <div className="eyebrow mb-2">Tiers</div>
              <ul className="text-sm space-y-1">
                {s.tiers.map((t) => (
                  <li key={t.name} className="flex items-baseline justify-between border-b border-border py-2 last:border-b-0">
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.description}</div>
                    </div>
                    <div className="text-right">
                      <div>${Number(t.price).toFixed(0)}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{t.duration_minutes}m</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="eyebrow">Deposit</span>
                <span className="text-sm">{Number(s.deposit_percent || 0).toFixed(0)}%</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(s)}
                  className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-border hover:border-foreground/60"
                  data-testid={`edit-${s.slug}-btn`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={() => setConfirmDelete(s)}
                  className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-destructive/40 text-destructive hover:bg-destructive/5"
                  data-testid={`delete-${s.slug}-btn`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {deleted.length > 0 && (
        <div className="pt-4" data-testid="recently-deleted-section">
          <div className="eyebrow mb-1">Recently deleted</div>
          <p className="text-xs text-muted-foreground mb-4">
            Deleted packages stay here for 30 days before being removed permanently.
          </p>
          <div className="space-y-2">
            {deleted.map((d) => {
              const daysLeft = Math.max(0, 30 - Math.floor((Date.now() - new Date(d.deleted_at)) / 86400000));
              return (
                <div
                  key={d.id}
                  className="flex items-center gap-4 border border-border bg-muted/40 px-4 py-3"
                  data-testid={`deleted-service-${d.slug}`}
                >
                  {d.image_url && <img src={d.image_url} alt={d.name} className="w-10 h-12 object-cover opacity-60" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {d.category} · {daysLeft} day{daysLeft === 1 ? "" : "s"} left to restore
                    </div>
                  </div>
                  <button
                    onClick={() => restore(d)}
                    className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-border hover:border-foreground/60"
                    data-testid={`restore-${d.slug}-btn`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-service-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-light">
              Delete &ldquo;{confirmDelete?.name}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The package will move to Recently Deleted, where you can restore it for 30 days.
              It disappears from your public site immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(confirmDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm-btn"
            >
              Delete package
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ServiceDialog
        open={!!editing || creating}
        onClose={() => { setEditing(null); setCreating(false); }}
        original={editing}
        onSave={(draft) => save(editing, draft, editing ? "Package updated" : "Package created")}
      />
    </div>
  );
}

function ServiceDialog({ open, onClose, original, onSave }) {
  const [draft, setDraft] = useState(emptyDraft);

  useEffect(() => {
    if (open) {
      setDraft(original ? JSON.parse(JSON.stringify(original)) : { ...emptyDraft, tiers: [{ ...emptyDraft.tiers[0] }] });
    }
  }, [open, original]);

  function updateTier(idx, key, value) {
    const tiers = [...draft.tiers];
    tiers[idx] = { ...tiers[idx], [key]: value };
    setDraft({ ...draft, tiers });
  }
  function removeTier(idx) {
    setDraft({ ...draft, tiers: draft.tiers.filter((_, i) => i !== idx) });
  }
  function addTier() {
    setDraft({
      ...draft,
      tiers: [...draft.tiers, { name: "", price: 0, duration_minutes: 60, description: "", deliverables: [] }],
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light" data-testid="service-dialog-title">
            {original ? `Edit ${original.name}` : "New package"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the package details, image, pricing tiers and deposit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Image preview + URL */}
          <div className="grid grid-cols-3 gap-4 items-start">
            <div className="aspect-[4/5] bg-muted overflow-hidden flex items-center justify-center">
              {draft.image_url ? (
                <img src={draft.image_url} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div className="col-span-2 space-y-3">
              <UploadField
                value={draft.image_url}
                onChange={(url) => setDraft({ ...draft, image_url: url })}
              />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Upload from your computer, or paste any public image URL (Unsplash, Pexels, etc.).
              </p>
            </div>
          </div>

          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name *">
              <input data-testid="service-name" required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm" />
            </Field>
            <Field label="Slug *">
              <input data-testid="service-slug" required value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm font-mono" />
            </Field>
            <Field label="Category">
              <input data-testid="service-category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm" />
            </Field>
            <Field label="Deposit (%) *">
              <input data-testid="service-deposit" type="number" min="1" max="100" value={draft.deposit_percent} onChange={(e) => setDraft({ ...draft, deposit_percent: e.target.value })} className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm" placeholder="e.g. 25" />
            </Field>
          </div>

          <Field label="Description">
            <textarea data-testid="service-description" rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm" />
          </Field>

          {/* Tiers */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="eyebrow">Tiers</span>
              <button onClick={addTier} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] hover:underline" data-testid="add-tier-btn">
                <Plus className="w-3.5 h-3.5" /> Add tier
              </button>
            </div>
            <div className="space-y-3">
              {draft.tiers.map((t, idx) => (
                <div key={idx} className="border border-border p-4 grid grid-cols-12 gap-3 items-end" data-testid={`tier-row-${idx}`}>
                  <div className="col-span-3">
                    <Field label="Name">
                      <input value={t.name} onChange={(e) => updateTier(idx, "name", e.target.value)} className="mt-1 w-full bg-transparent border-b border-border py-1.5 outline-none focus:border-foreground text-sm" data-testid={`tier-${idx}-name`} />
                    </Field>
                  </div>
                  <div className="col-span-2">
                    <Field label="Price">
                      <input type="number" value={t.price} onChange={(e) => updateTier(idx, "price", e.target.value)} className="mt-1 w-full bg-transparent border-b border-border py-1.5 outline-none focus:border-foreground text-sm" data-testid={`tier-${idx}-price`} />
                    </Field>
                  </div>
                  <div className="col-span-2">
                    <Field label="Minutes">
                      <input type="number" value={t.duration_minutes} onChange={(e) => updateTier(idx, "duration_minutes", e.target.value)} className="mt-1 w-full bg-transparent border-b border-border py-1.5 outline-none focus:border-foreground text-sm" data-testid={`tier-${idx}-minutes`} />
                    </Field>
                  </div>
                  <div className="col-span-4">
                    <Field label="Description">
                      <input value={t.description} onChange={(e) => updateTier(idx, "description", e.target.value)} className="mt-1 w-full bg-transparent border-b border-border py-1.5 outline-none focus:border-foreground text-sm" data-testid={`tier-${idx}-desc`} />
                    </Field>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button onClick={() => removeTier(idx)} className="p-2 hover:bg-muted text-muted-foreground" aria-label="Remove tier" data-testid={`tier-${idx}-remove`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-5 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60" data-testid="service-cancel-btn">
            Cancel
          </button>
          <button onClick={() => onSave(draft)} className="px-5 h-10 text-xs uppercase tracking-[0.18em] bg-primary text-primary-foreground hover:bg-primary/90" data-testid="service-save-btn">
            Save package
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      {children}
    </label>
  );
}

function UploadField({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image is larger than 15 MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-2">
      <span className="eyebrow">Image</span>
      <div className="flex items-center gap-2">
        <input
          data-testid="service-image-url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or upload →"
          className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid="service-image-file"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-60"
          data-testid="service-upload-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
    </div>
  );
}
